// services/costAllocation.js — разпределяне на разходите (транспорт, мито,
// други) върху стоките от ЕДНА доставка (Макс, 19.10.2026).
//
// Точно както в таблицата на кантората („разпределяне на разходи"): всеки
// вид разход се разпределя ОТДЕЛНО, пропорционално на стойността на реда в
// евро (претеглен дял); новата стойност на реда = стойност + дяловете ⇒
// единичната доставна цена = нова стойност / количество (СС 2 — доставната
// стойност включва мита, транспорт и другите преки разходи).
//
// Разлика от таблицата: там ROUND на всеки ред губи понякога 0,01 €
// (MIRSAN: 2 500,00 → 2 499,99). Тук остатъкът от закръглянето отива на
// реда с най-голяма стойност, за да е сборът точно колкото разхода.
//
// Само по документи, прикачени към същата доставка (решение на Макс —
// „да си опростим живота"): разходът носи file_id на придружаващ документ.
//
// Чиста функция, без база — ползват я stockLink.planPurchase (цената на
// постъплението) и екранът на доставката (таблицата за преглед).

const { round2 } = require('./totals');
const stock = require('./stock');

const KINDS = ['transport', 'customs', 'other'];
const KIND_LABEL = { transport: 'Транспорт', customs: 'Мито', other: 'Други' };

// Разходите на документа, изчистени: само познат вид, сума > 0.
function cleanCosts(costs) {
  return (Array.isArray(costs) ? costs : [])
    .map((c) => ({ kind: KINDS.includes(c && c.kind) ? c.kind : 'other', amount: round2(Number(c && c.amount) || 0), file_id: c && c.file_id != null ? Number(c.file_id) : null }))
    .filter((c) => c.amount > 0);
}

// Сбор по вид: { transport, customs, other }.
function totalsByKind(costs) {
  const t = { transport: 0, customs: 0, other: 0 };
  for (const c of cleanCosts(costs)) t[c.kind] = round2(t[c.kind] + c.amount);
  return t;
}

// Разделя amount по тежестите (стойностите) — до стотинка, сборът е точен.
function split(amount, weights) {
  const total = weights.reduce((s, w) => s + w, 0);
  const out = weights.map(() => 0);
  if (!(amount > 0) || !(total > 0)) return out;
  let biggest = 0;
  weights.forEach((w, i) => {
    out[i] = round2(amount * w / total);
    if (w > weights[biggest]) biggest = i;
  });
  out[biggest] = round2(out[biggest] + round2(amount - out.reduce((s, v) => s + v, 0)));
  return out;
}

// Кои редове поемат разход: редовете със складов артикул и количество > 0
// (същите, които влизат в склада). Връща индексите им.
function eligibleLines(doc, articlesById, mode) {
  const lines = Array.isArray(doc && doc.lines) ? doc.lines : [];
  const out = [];
  lines.forEach((l, i) => {
    if (l.article_id == null || !((Number(l.quantity) || 0) > 0)) return;
    const a = articlesById ? articlesById.get(Number(l.article_id)) : null;
    if (stock.isStockArticle(a, mode)) out.push(i);
  });
  return out;
}

// Разпределението за документа:
//   { rows: [{ line, value, share, transport, customs, other, added, newValue, qty, unitPrice }],
//     totals: { value, transport, customs, other, added, newValue }, byLine: Map(line → added) }
// value — стойността на реда в евро (subtotal × курс); share — дял в %.
function allocate(doc, articlesById, mode) {
  const rate = Number(doc && doc.exchange_rate) > 0 ? Number(doc.exchange_rate) : 1;
  const lines = Array.isArray(doc && doc.lines) ? doc.lines : [];
  const idx = eligibleLines(doc, articlesById, mode);
  const values = idx.map((i) => round2(Math.abs(Number(lines[i].subtotal) || 0) * rate));
  const sumV = round2(values.reduce((s, v) => s + v, 0));
  const t = totalsByKind(doc && doc.costs);
  const parts = {};
  for (const k of KINDS) parts[k] = split(t[k], values);
  const rows = idx.map((i, j) => {
    const added = round2(parts.transport[j] + parts.customs[j] + parts.other[j]);
    const qty = Number(lines[i].quantity) || 0;
    const newValue = round2(values[j] + added);
    return {
      line: i, description: lines[i].description || '', value: values[j],
      share: sumV > 0 ? Math.round((values[j] / sumV) * 100000) / 1000 : 0,
      transport: parts.transport[j], customs: parts.customs[j], other: parts.other[j],
      added, newValue, qty, unit: lines[i].unit || '', unitPrice: qty ? Math.round((newValue / qty) * 10000) / 10000 : 0,
    };
  });
  const sum = (k) => round2(rows.reduce((s, r) => s + r[k], 0));
  const byLine = new Map(rows.map((r) => [r.line, r.added]));
  return {
    rows, byLine,
    totals: { value: sumV, transport: sum('transport'), customs: sum('customs'), other: sum('other'), added: sum('added'), newValue: sum('newValue') },
    unallocated: rows.length ? 0 : round2(t.transport + t.customs + t.other),
  };
}

module.exports = { KINDS, KIND_LABEL, cleanCosts, totalsByKind, split, eligibleLines, allocate };
